import {
  Autocomplete,
  AutocompleteChangeReason,
  AutocompleteInputChangeReason,
  AutocompleteProps,
  TextField,
  Typography,
  createFilterOptions,
} from '@mui/material';
import { Product } from '@protos/product';
import { capitalise } from '@utils/formatter';
import { useAppStore } from '../store/AppStore';
import { useState } from 'react';

// Warning: possibly hairy type juggling

// There's a few aims with the below:
// - provide sensible defaults
// - still allow you to override things if required
// - ensure that the value has to be provided
// - and, finally: stop the TypeScript compiler complaining

type PropsForAutocomplete = AutocompleteProps<Product, false, false, false, 'div'>;

type ProductAutocompleteProps = Partial<PropsForAutocomplete> & {
  onProductSelected: (product: Product | null) => void;
  value: Product | null;
};

const ProductAutoCompleteFilterOptions = createFilterOptions({
  matchFrom: 'any',
  stringify: (p: Product) => {
    return [p.symbol, p.product_group, p.full_name].join('_');
  },
});

// If you've got a better way of expressing this then please feel free to implement it
type AutoCompleteDefaultProps = Pick<PropsForAutocomplete, 'renderOption' | 'renderInput'> & Omit<PropsForAutocomplete, 'options' | 'value'>;

const componentDefaults: AutoCompleteDefaultProps = {
  getOptionLabel: o => (typeof o === 'string' ? o : o.symbol),
  renderOption: (props, option: Product) => {
    const { key, ...optionProps } = props;
    return (
      <li key={key} {...optionProps}>
        <span>{option.symbol.toUpperCase()}</span>
        <Typography variant="body" sx={{ textAlign: 'right' }}>
          {option.full_name}
        </Typography>
      </li>
    );
  },
  filterOptions: ProductAutoCompleteFilterOptions,
  selectOnFocus: true,
  clearOnEscape: true,
  clearOnBlur: true,
  autoCapitalize: 'on',
  handleHomeEndKeys: true,
  isOptionEqualToValue: (option: Product, value: Product) => {
    return option.symbol === value.symbol;
  },
  renderInput: params => {
    return <TextField {...params} label="Product" />;
  },
  autoComplete: true,
  autoHighlight: true,
  fullWidth: true,
  freeSolo: false,
  groupBy: p => capitalise(p.product_group, '_', ' '),
} as const;

export const ProductAutocomplete = (props: ProductAutocompleteProps) => {
  const { value, onProductSelected, ...propsForAutoComplete } = props;
  const propsWithDefaults = { ...componentDefaults, ...propsForAutoComplete };

  const { products } = useAppStore();

  const [searchString, setSearchString] = useState<string>('');

  const onSelectionChange = (e: React.SyntheticEvent<Element, Event>, value: Product | null, reason: AutocompleteChangeReason) => {
    onProductSelected(value);
    setSearchString('');
  };

  const onAutocompleteInputChange = (e: React.SyntheticEvent, value: string, reason: AutocompleteInputChangeReason) => {
    if (reason === 'selectOption') {
      return;
    }
    setSearchString(value);
  };

  return (
    <Autocomplete
      {...propsWithDefaults}
      value={value}
      options={products || []}
      inputValue={searchString}
      onInputChange={onAutocompleteInputChange}
      onChange={onSelectionChange}
      style={{ marginLeft: '0px' }}
    />
  );
};
